import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';

import Radio from '@mui/material/Radio';

import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import RadioGroup from "@mui/material/RadioGroup";


import { v4 as uuid } from 'uuid';
import { removeItemWithSlice } from 'util/utils'



const useStyles = makeStyles(theme => ({
    root: {
        minWidth: 275,
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    bullet: {
        display: 'inline-block',
        margin: '0 2px',
        transform: 'scale(0.8)',
    },
    title: {
        fontSize: 14,
    },
    pos: {
        marginBottom: 12,
    },
    buttonLowCase: {
        textTransform: "none"
    }
}));

export const DINEIN_TYPE = 'dinein';

/** A dine-in entry is only saveable once it names the table the POS has to match. */
export const hasTableNumber = (table) =>
    String(table.realTableNumber ?? '').trim() !== '';

/** Entries the merchant still has to finish before the kiosk can be saved. */
export const incompleteDineInTables = (tables = []) =>
    tables.filter((table) => (table.type || table.tableId) === DINEIN_TYPE && !hasTableNumber(table));

const NewTables = (props) => {
    const { qrTypes } = props;

    const classes = useStyles();


    const [tables, setTables] = useState([]);
    const [orderCode, setOrderCode] = useState("");
    const [tableNumber, setTableNumber] = useState("");
    const [tableName, setTableName] = useState("");

    /**
     * The type a saved entry was configured as.
     *
     * `type` is what the radio reads, and it is deliberately NOT the same field as `tableId`:
     * a dine-in entry's `tableId` is the encrypted real table number, which the server derives
     * on save. The server sends `type` back on load, so the fallback here only covers an entry
     * being edited in a session that loaded before that shipped.
     */
    const typeOf = (table) => table.type || table.tableId;

    const isDineIn = (table) => typeOf(table) === DINEIN_TYPE;

    const handleCodeChange = (value, id) => {
        // find qr type by code
        const qrType = qrTypes.find(qrType => qrType.code === value);

        let newList = [...tables];
        const index = newList.findIndex(size => size.id === id);
        if (index !== -1) {
            let size = newList[index];
            // `tableId` is still set to the code so nothing changes for the fixed service types.
            // For dine-in the server replaces it with the encrypted table number on save.
            newList[index] = { ...size, type: qrType.code, tableNumber: qrType.name, tableId: qrType.code };
            updateTables(newList, true);
        }
    };


    useEffect(() => {

        setTables(props.tableList);


    }, [props]);

    const updateTables = (newSizes, needUpdateParent) => {
        const theNewSizes = newSizes.map((item, index) => ({ ...item, sort: index }));
        setTables(theNewSizes);
        if (needUpdateParent) {
            props.updateTables(theNewSizes)
        }
    };


    const addNewTable = () => {
        let newList = [...tables];
        newList.push({ id: uuid(), tableNumber: '', tableId: '', type: '' });
        updateTables(newList, false);
    };

    const updateName = (value, id) => {
        let newList = [...tables];
        const index = newList.findIndex(size => size.id === id);
        if (index !== -1) {
            let size = newList[index];
            newList[index] = { ...size, tableNumber: value };
            updateTables(newList, true);
        }
    };

    const updateRealTableNumber = (value, id) => {
        let newSizes = [...tables];
        const index = newSizes.findIndex(size => size.id === id);
        if (index !== -1) {
            let size = newSizes[index];

            newSizes[index] = { ...size, realTableNumber: value };
            updateTables(newSizes, true);
        }
    };

    const deleteTable = (id) => {
        const index = tables.findIndex(size => size.id === id);
        if (index !== -1) {
            const newSizes = removeItemWithSlice(tables, index);
            updateTables(newSizes, true);
        }
    };



    return (

        <Grid item container direction="column" spacing={2} >


            {tables && tables.length > 0 ? <>
                {tables.map((table) => {
                    return <Grid item container direction="row" spacing={1} key={table.id} alignItems="center">



                        <Grid item md={12} xs={12}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Type</FormLabel>
                                <RadioGroup aria-label="choose type" name="radio-buttons-group" row onChange={e => handleCodeChange(e.target.value, table.id)}>
                                    {qrTypes.map((tableType) => (
                                        <FormControlLabel
                                            key={tableType.code}
                                            value={tableType.code}
                                            control={<Radio checked={typeOf(table) === String(tableType.code)} />}
                                            label={`${tableType.name}`}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid item xs={2}>
                            <TextField 
                                fullWidth
                                label="Name"
                                margin="dense"
                                name="tableName"
                                variant="outlined"
                                value={table.tableNumber}
                                onChange={e => updateName(e.target.value, table.id)}
                            />
                        </Grid>


                        {isDineIn(table) ?

                            <Grid item xs={2}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Table Number"
                                    margin="dense"
                                    name="tableNumber"
                                    variant="outlined"
                                    value={table.realTableNumber || ''}
                                    error={!hasTableNumber(table)}
                                    // The POS matches a dine-in order to a table by this exact
                                    // number. Without it the order reaches the counter and is
                                    // dropped, so saving one is blocked.
                                    helperText={hasTableNumber(table) ? '' : 'Required for Dine In'}
                                    onChange={e => updateRealTableNumber(e.target.value, table.id)}
                                />
                            </Grid>
                            : null}

                        <Grid item xs={2}>
                            <IconButton aria-label="more" className={classes.margin} edge='end' color="secondary" onClick={e => deleteTable(table.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Grid>

                    </Grid>
                })}
                <Grid item xs={12} md={12}>
                    <Button className={classes.buttonLowCase} variant="outlined" color="primary" size="small" onClick={addNewTable}>Add type</Button>
                </Grid>
            </> :
                <Grid item xs={4}>
                    <Button className={classes.buttonLowCase} variant="outlined" color="primary" onClick={addNewTable} size="medium">Add types</Button>
                </Grid>
            }
        </Grid>

    );
};

export default NewTables;
